import React, { useMemo } from 'react';
import type { CompatDistributionBin } from '../types';

interface DistributionChartProps {
  distribution: CompatDistributionBin[];
  color?: string;
  unitLabel: string;
}

/**
 * SVG bar chart matching sample-ui.html.
 * Every bar gets an orders label; only x-axis pipe labels are thinned when dense.
 */
export const DistributionChart: React.FC<DistributionChartProps> = ({
  distribution,
  color = '#1F9A3A',
  unitLabel,
}) => {
  const { bars, W, H, pad } = useMemo(() => {
    const width = 880;
    const height = 200;
    const padding = 32;
    if (!distribution?.length) {
      return { bars: [] as React.ReactNode[], W: width, H: height, pad: padding };
    }

    const n = distribution.length;
    const bw = (width - 2 * padding) / n;
    const top = Math.max(...distribution.map((d) => d.orders)) || 1;

    // Keep bar-value labels in the same size/weight family as axis ticks.
    const valueFont = n > 40 ? 6.5 : n > 24 ? 7.5 : n > 14 ? 8 : 9;
    const axisFont = n > 40 ? 6.5 : n > 24 ? 7.5 : n > 14 ? 8 : 9;
    // Sample: every bar when ≤14, otherwise every other — denser sets skip more.
    const axisEvery = n <= 14 ? 1 : n > 50 ? 3 : 2;

    const nodes = distribution.map((d, i) => {
      const h = (d.orders / top) * (height - 60);
      const x = padding + i * bw;
      const y = height - 30 - h;

      return (
        <g key={`${d.pipes}-${i}`}>
          <rect
            x={x + 2}
            y={y}
            width={Math.max(2, bw - 4)}
            height={h}
            fill={color}
            rx={3}
          />
          <text
            x={x + bw / 2}
            y={Math.max(9, y - 3)}
            fontSize={valueFont}
            fill="#969696"
            textAnchor="middle"
          >
            {d.orders}
          </text>
          {i % axisEvery === 0 && (
            <text
              x={x + bw / 2}
              y={height - 12}
              fontSize={axisFont}
              fill="#969696"
              textAnchor="middle"
            >
              {d.pipes}
            </text>
          )}
        </g>
      );
    });
    return { bars: nodes, W: width, H: height, pad: padding };
  }, [distribution, color]);

  if (!distribution?.length) return null;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto block">
      {bars}
      <line x1={pad} y1={H - 30} x2={W - pad} y2={H - 30} stroke="#E2E5EA" />
      <text x={W / 2} y={H - 1} fontSize={8} fill="#969696" textAnchor="middle">
        {unitLabel}
      </text>
    </svg>
  );
};
